import React, { useState, useEffect, useContext, useMemo } from "react";
import ApiService from "../api/apiService";
import showSwal from "./swal";
import { common } from "../util/common";
import { WebSocketContext, ChatContext } from "../context/contexts";
import { getTimeAgo } from "../util/helperFunctions";
import ModalShell from "./modal/ModalShell";
import UserAvatar from "./modal/UserAvatar";
import UserPicker from "./modal/UserPicker";
import { EmptyState } from "./modal/EmptyState";

function ViewMembersModal({
  conversationData,
  showHideViewMembersModal,
  updateConversationData,
  userDetails,
  conversationUserPermission,
}) {
  const conversation_id = conversationData?.id;
  const conversation_name = conversationData?.name;
  const conversation_members = useMemo(() => conversationData?.members || [], [conversationData?.members]);
  const isSelfAdmin = !!conversationUserPermission?.is_admin;

  const [allUsers, setAllUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [toAdd, setToAdd] = useState([]);
  const [adding, setAdding] = useState(false);
  const [confirmRemoveId, setConfirmRemoveId] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const WS = useContext(WebSocketContext);
  const Chat = useContext(ChatContext);

  // fetched once; members are excluded locally so it stays correct after add/remove
  useEffect(() => {
    let cancelled = false;
    const fetchUserNames = async () => {
      try {
        const response = await ApiService.users.getAllusers();
        const { success, users } = response;
        if (cancelled) return;
        if (success) setAllUsers(users);
        else showSwal(common.errors.CONVERSATION_FETCH_USERNAME_ERROR);
      } catch (error) {
        console.error("Error fetching user names:", error);
      } finally {
        if (!cancelled) setUsersLoading(false);
      }
    };
    if (isSelfAdmin) fetchUserNames();
    else setUsersLoading(false);
    return () => {
      cancelled = true;
    };
  }, [isSelfAdmin]);

  const memberUserIds = useMemo(() => new Set(conversation_members.map((m) => m?.user?.id)), [conversation_members]);
  const availableUsers = useMemo(() => allUsers.filter((u) => !memberUserIds.has(u.id)), [allUsers, memberUserIds]);

  const onlineMap = useMemo(() => {
    const map = new Map();
    (Chat.onlineUsers || []).forEach((o) => map.set(o.user_id, o));
    return map;
  }, [Chat.onlineUsers]);

  const onlineCount = conversation_members.filter((m) => onlineMap.get(m?.user?.id)?.is_online).length;

  const visibleMembers = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return conversation_members;
    return conversation_members.filter((m) =>
      `${m?.user?.display_name || ""} ${m?.user?.email_address || ""}`.toLowerCase().includes(q)
    );
  }, [conversation_members, filter]);

  const handleMemberViewActions = async (conversation_member_id, action) => {
    if (busyId) return;
    setBusyId(conversation_member_id);
    try {
      const response = await ApiService.conversationMembers.updateConversationMember(
        conversation_id,
        conversation_member_id,
        { action }
      );

      const { success, conversation_action } = response;
      if (success) {
        WS.websocketInstance.sendMessage({ message: conversation_action, type: "user-actions-chat" });
        updateConversationData(conversation_id);
      } else {
        showSwal(common.errors.UPDATE_CONVERSATION_MEMBER_ERROR);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setBusyId(null);
      setConfirmRemoveId(null);
    }
  };

  const handleMembersAdd = async () => {
    if (adding || toAdd.length === 0) return;
    setAdding(true);
    const failed = [];
    for (const user of toAdd) {
      try {
        const response = await ApiService.conversationMembers.addConversationMember(conversation_id, {
          user_id: user.id,
        });
        const { success, conversation_action } = response;
        if (success) {
          WS.websocketInstance.sendMessage({ message: conversation_action, type: "user-actions-chat" });
        } else {
          failed.push(user);
        }
      } catch (err) {
        console.error(err);
        failed.push(user);
      }
    }
    updateConversationData(conversation_id);
    setToAdd(failed); // keep only the ones that did not go through
    if (failed.length > 0) showSwal(common.errors.ADD_CONVERSATION_MEMBER_ERROR);
    else setShowAdd(false);
    setAdding(false);
  };

  const addSection = isSelfAdmin && (
    <section className='bh-modal__header-extra' style={{ borderBottom: "1px solid #e3e7ec" }}>
      {!showAdd ? (
        <button type='button' className='bh-btn bh-btn--ghost bh-btn--sm' onClick={() => setShowAdd(true)}>
          <i className='fa fa-user-plus' aria-hidden='true'></i> Add members
        </button>
      ) : (
        <>
          <div className='bh-section-title'>
            <span>Add members</span>
            <button
              type='button'
              className='bh-btn bh-btn--ghost bh-btn--sm'
              onClick={() => {
                setShowAdd(false);
                setToAdd([]);
              }}
              disabled={adding}
            >
              Cancel
            </button>
          </div>
          <UserPicker
            users={availableUsers}
            selected={toAdd}
            loading={usersLoading}
            disabled={adding}
            onAdd={(u) => setToAdd((prev) => [...prev, u])}
            onRemove={(u) => setToAdd((prev) => prev.filter((p) => p.id !== u.id))}
          />
          {toAdd.length > 0 && (
            <button
              type='button'
              className='bh-btn bh-btn--primary'
              style={{ marginTop: 10, width: "100%" }}
              onClick={handleMembersAdd}
              disabled={adding}
            >
              {adding && <span className='bh-spinner' />}
              {adding ? "Adding…" : `Add ${toAdd.length} member${toAdd.length === 1 ? "" : "s"}`}
            </button>
          )}
        </>
      )}
    </section>
  );

  return (
    <ModalShell
      title={`Members · ${conversation_members.length}`}
      subtitle={`${conversation_name || ""}${onlineCount ? ` · ${onlineCount} online` : ""}`}
      size='md'
      onClose={showHideViewMembersModal}
      bodyClassName='bh-modal__body--flush'
    >
      {addSection}
      {conversation_members.length > 6 && (
        <div className='bh-modal__header-extra'>
          <div className='bh-search'>
            <i className='fa fa-search bh-search__icon' aria-hidden='true'></i>
            <input
              type='text'
              className='bh-input bh-search__input'
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder='Filter members…'
              aria-label='Filter members'
            />
          </div>
        </div>
      )}
      {visibleMembers.length === 0 ? (
        <EmptyState icon='fa-users' title={filter ? "No members match" : "No members found"} />
      ) : (
        <ul className='bh-list'>
          {visibleMembers.map((member) => {
            const user = member?.user;
            const memberId = member?.id;
            const isMuted = member?.is_muted;
            const isSelf = user?.id === userDetails?.id;
            const onlineData = onlineMap.get(user?.id);
            const userOnline = !!onlineData?.is_online;
            const lastSeen = !userOnline && onlineData?.last_online ? getTimeAgo(onlineData.last_online) : null;
            const canManage = !isSelf && isSelfAdmin;
            const confirming = confirmRemoveId === memberId;

            return (
              <li
                key={memberId ?? user?.id}
                className={`bh-row bh-row--members ${busyId === memberId ? "bh-row--busy" : ""}`}
              >
                <UserAvatar user={user} size={40} online={userOnline} />
                <div className='bh-row__main'>
                  <div className='bh-row__top'>
                    <span className='bh-row__name'>{user?.display_name}</span>
                    {isSelf && <span className='bh-badge'>You</span>}
                    {member?.is_admin && <span className='bh-badge bh-badge--brand'>Admin</span>}
                    {isMuted && <span className='bh-badge bh-badge--warn'>Muted</span>}
                  </div>
                  <div className='bh-row__sub'>{user?.email_address}</div>
                  <div className='bh-row__sub'>
                    {userOnline ? (
                      <span style={{ color: "#17803f" }}>Online</span>
                    ) : lastSeen ? (
                      `Active ${lastSeen} ago`
                    ) : (
                      "Offline"
                    )}
                  </div>
                </div>
                {canManage && (
                  <div className='bh-row__actions'>
                    {confirming ? (
                      <div className='bh-confirm'>
                        <span>Remove {user?.display_name}?</span>
                        <button
                          type='button'
                          className='bh-btn bh-btn--ghost bh-btn--sm'
                          onClick={() => setConfirmRemoveId(null)}
                        >
                          Cancel
                        </button>
                        <button
                          type='button'
                          className='bh-btn bh-btn--danger bh-btn--sm'
                          onClick={() => handleMemberViewActions(memberId, "remove")}
                        >
                          Remove
                        </button>
                      </div>
                    ) : (
                      <>
                        <button
                          type='button'
                          className='bh-btn bh-btn--ghost bh-btn--sm'
                          onClick={() => handleMemberViewActions(memberId, "mute")}
                        >
                          <i className={`fa ${isMuted ? "fa-volume-up" : "fa-volume-off"}`} aria-hidden='true'></i>
                          {isMuted ? "Unmute" : "Mute"}
                        </button>
                        <button
                          type='button'
                          className='bh-btn bh-btn--text-danger bh-btn--sm'
                          onClick={() => setConfirmRemoveId(memberId)}
                        >
                          Remove
                        </button>
                      </>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </ModalShell>
  );
}

export default ViewMembersModal;
